import api from "./client";

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function unwrapData(response) {
  if (response && typeof response === "object" && "data" in response) {
    return response.data;
  }

  return response;
}

function isNumericReference(value) {
  return /^\d+$/.test(String(value ?? "").trim());
}

export function getJobTitle(job, isAr = false) {
  if (!job) return "";

  return isAr
    ? job.title_ar || job.title_en || `Job #${job.id ?? ""}`
    : job.title_en || job.title_ar || `Job #${job.id ?? ""}`;
}

export function getJobSummary(job, isAr = false) {
  if (!job) return "";

  return isAr
    ? job.short_description_ar || job.description_ar || job.short_description_en || job.description_en || ""
    : job.short_description_en || job.description_en || job.short_description_ar || job.description_ar || "";
}

export function getJobDescription(job, isAr = false) {
  if (!job) return "";

  return isAr
    ? job.description_ar || job.description_en || ""
    : job.description_en || job.description_ar || "";
}

export function getJobSalaryLabel(job, isAr = false) {
  if (!job) {
    return isAr ? "غير محدد" : "Not specified";
  }

  if (job.salary_text) {
    return job.salary_text;
  }

  const min = Number(job.salary_min ?? 0);
  const max = Number(job.salary_max ?? 0);

  if (min > 0 && max > 0) {
    return `$${min.toFixed(min % 1 === 0 ? 0 : 2)} - $${max.toFixed(max % 1 === 0 ? 0 : 2)}`;
  }

  if (min > 0) {
    return `${isAr ? "ابتداءً من" : "From"} $${min.toFixed(min % 1 === 0 ? 0 : 2)}`;
  }

  if (max > 0) {
    return `${isAr ? "حتى" : "Up to"} $${max.toFixed(max % 1 === 0 ? 0 : 2)}`;
  }

  return isAr ? "غير محدد" : "Not specified";
}

export function formatJobDate(value, lang = "en") {
  if (!value) return "";

  try {
    return new Date(String(value).replace(" ", "T")).toLocaleString(
      lang === "ar" ? "ar" : "en-US",
      {
        year: "numeric",
        month: "short",
        day: "numeric",
      }
    );
  } catch {
    return String(value);
  }
}

export async function listJobs(options = {}) {
  const response = await api.apiGet("jobs", {
    cache: options.cache ?? true,
    ttl: options.ttl ?? 30_000,
  });

  return asArray(unwrapData(response));
}

export async function resolveJobId(jobReference, knownJobs = null) {
  const raw = String(jobReference ?? "").trim();

  if (isNumericReference(raw)) {
    return Number(raw);
  }

  const jobs = knownJobs || (await listJobs({ cache: true, ttl: 15_000 }));
  const normalized = raw.toLowerCase();
  const matched = jobs.find(
    (job) =>
      String(job?.slug || "").toLowerCase() === normalized ||
      String(job?.id || "") === raw
  );

  if (!matched?.id) {
    throw { status: 404, message: "Job not found" };
  }

  return Number(matched.id);
}

export async function getJobDetails(jobReference) {
  const resolvedId = isNumericReference(jobReference)
    ? Number(jobReference)
    : await resolveJobId(jobReference);

  const response = await api.apiGet(`jobs/${resolvedId}`);
  return unwrapData(response);
}

export async function applyToJob(jobId, payload = {}) {
  return api.apiPost(`jobs/${jobId}/apply`, payload);
}

export async function getMyJobApplications() {
  const response = await api.apiGet("my-job-applications");
  return asArray(unwrapData(response));
}
